/**
 * `src/platform/` — the shell's http client (WS-C §2.2).
 *
 * ⛔ WRITTEN FROM SCRATCH rather than adopted from the old console's `api/client`, for one
 * measured reason: that module hardcoded `window.location.href = '/login'` on the
 * 401-after-a-failed-refresh path and the target was not injectable. At the time `/login` on
 * this origin was the OLD console, so reusing it would have meant every session expiry landing
 * the operator in the app we were replacing. WS-C §2.6 states the rule for `logout`; this is
 * the same rule on the path nobody clicks deliberately, which makes it the more likely one to
 * ship unnoticed.
 *
 * ⚠️ The old console is gone and `/login` is now unambiguously ours, so the hazard that
 * motivated this is retired — but the SHAPE stays: the target comes from the shared
 * `LOGIN_URL` constant, never a literal typed here. That is what carried this file correctly
 * through the root cutover without anyone editing it.
 *
 * ⚠️ Everything else here is deliberately the same SHAPE as core's, because each clause was
 * earned there. The differences are the login URL and the per-call retry flag.
 */
import { ACTIVE_ROLE_SWITCH_PATH } from '@zion/sdk-contracts';
import { getApiBaseUrl } from '../runtime-config';
import { LOGIN_URL } from '../paths';
import { consoleClientHeaders } from './client-label';

type TokenRefreshFn = () => Promise<string | null>;

/**
 * ⛔ The access token lives in MODULE MEMORY ONLY. Never in `localStorage`.
 *
 * ⛔ AND NEITHER DOES THE REFRESH TOKEN ANY MORE. cp-auth-api sets `cp_refresh_token` as an
 * HttpOnly cookie under `X-Zion-Session-Transport: cookie` (decision D20), so script reads
 * nothing at all. `../auth/tokens.ts` keeps the legacy `localStorage` key for exactly one
 * release, to migrate out of it and to carry an N-1 pod's rotated token through the roll.
 *
 * ⛔ THE COOKIE IS THE CREDENTIAL THE 401 REPLAY BELOW RELIES ON. `refreshFn` is
 * `useAuth.doRefresh`, which posts `/v1/auth/refresh` with `{}` and the transport header; the
 * browser attaches the cookie itself because the request is same-origin and sits under the
 * cookie's `Path=/v1/auth` (default F7). ⛔ NO `credentials` OPTION IS WRITTEN HERE, and adding
 * one would only buy a CORS requirement this deployment does not have.
 */
let accessToken: string | null = null;
let refreshFn: TokenRefreshFn | null = null;

/**
 * ⛔ COALESCES concurrent refreshes, and it is NOT an optimisation.
 *
 * The server ROTATES the refresh token, so two parallel 401s each firing their own
 * `POST /v1/auth/refresh` means the second presents a token the first already invalidated:
 * both callers 401 and the session dies, intermittently, in a way that looks like a server
 * fault. One in-flight promise, awaited by every racer.
 */
let refreshInFlight: Promise<string | null> | null = null;

/**
 * The bound of one coalesced refresh, in ms: 30 s. A refresh that has not settled by then rejects
 * every caller that awaits it with {@link TokenRefreshTimeoutError} and frees the slot, so the next
 * 401 starts a new refresh. Without the bound, one refresh that never answers (a stalled connection)
 * holds the slot for good, and every later 401 in the tab waits on it until a page reload.
 *
 * A late answer of the abandoned refresh still lands its token through `useAuth`'s `runRefresh`. A new
 * refresh first waits for the cross-tab refresh lock that the abandoned one holds, for up to
 * `LOCK_ACQUIRE_TIMEOUT_MS` (`refresh-lock.ts`). That is the trade the lock already makes against a
 * wedged holder: the race risks one session, a hang costs every request.
 */
export const REFRESH_TIMEOUT_MS = 30_000;

/**
 * The rejection of a request whose 401 refresh did not settle in {@link REFRESH_TIMEOUT_MS}. Its
 * `message` is the sentence a pane shows. The request was not replayed, so it changed nothing on the
 * server after its 401.
 */
export class TokenRefreshTimeoutError extends Error {
  constructor() {
    super(`The session refresh did not answer in ${REFRESH_TIMEOUT_MS / 1000} seconds, so the request was not sent again. Try again.`);
    this.name = 'TokenRefreshTimeoutError';
  }
}

function coalescedRefresh(fn: TokenRefreshFn): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const bound = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new TokenRefreshTimeoutError()), REFRESH_TIMEOUT_MS);
  });
  refreshInFlight = (async () => {
    try {
      // `Promise.race` keeps a handler on `fn()`, so a late rejection of an abandoned refresh is
      // never unhandled.
      return await Promise.race([fn(), bound]);
    } finally {
      // `finally`, not the resolve path: a THROWN or timed-out refresh must also clear the slot, or a
      // single failure wedges every later request on a permanently-rejected promise.
      clearTimeout(timer);
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/** Set the current access token for API calls. `null` clears it. */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/** The current access token, or `null`. */
export function getAccessToken(): string | null {
  return accessToken;
}

/** Register the refresh function the 401 path calls. */
export function setTokenRefreshFn(fn: TokenRefreshFn): void {
  refreshFn = fn;
}

/**
 * TEST-ONLY reset of the module singletons above.
 *
 * ⚠️ It exists because `vitest.config.ts` runs with the default `isolate: true` but the
 * three `let`s are module state: a suite that authenticates and a suite that asserts the
 * unauthenticated path would otherwise depend on file order. Named `__…ForTest` so it reads
 * as scaffolding at every call site, matching `__setDeploymentBrandingForTest` in the
 * package this one is derived from.
 */
export function __resetHttpClientForTest(): void {
  accessToken = null;
  refreshFn = null;
  refreshInFlight = null;
}

/** An HTTP failure. `status` plus the parsed body, because the body carries the message. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`API error ${status}`);
    this.name = 'ApiError';
  }
}

/**
 * Auth endpoints that must NEVER trigger the 401 → refresh → replay path.
 *
 * The refresh handler itself calls `POST /v1/auth/refresh` through this same wrapper.
 * Without this guard a 401 from the refresh endpoint recurses into another refresh attempt
 * — and since the outer refresh is still pending on `refreshInFlight`, the inner call awaits
 * the promise it is itself supposed to settle, and DEADLOCKS. `/login` and `/logout` are
 * here for the plainer reason that neither is ever worth retrying on a 401.
 *
 * ⛔ `/v1/auth/passkey/login` AND `/v1/auth/oauth/exchange` ARE THE TWO OTHER PUBLIC MINT ROUTES,
 * AND THEY ARE HERE FOR A SECURITY REASON (R-73). A 401 from a public mint route is the verdict on
 * the credential the caller just presented: a bad passkey assertion, a spent OAuth code. It says
 * nothing about the session this tab already holds, so refreshing that session and replaying the
 * mint rotates a live cookie on the strength of someone else's failed attempt. `/v1/auth/login` was
 * already here; with these two the set of public mint routes is complete.
 *
 * ⛔ `/v1/auth/passkey/login/options` IS A SEPARATE ENTRY AND HAS TO BE (R-73 as amended).
 * `isNoRetryPath` matches the whole path, or the path with a `?` suffix, so the parent entry
 * `/v1/auth/passkey/login` never covered it. The console calls it before a session exists, and its
 * 401 is the same kind of verdict: about the request, never about a session this tab holds.
 *
 * ⛔ `/v1/auth/change-password` IS HERE AS WELL, AND ITS ENTRY IS A DEADLOCK GUARD (R-76). The
 * route is authenticated, so a 401 there can genuinely be an expired bearer — but `useAuth`'s
 * `changePassword` now sends under the cross-tab refresh lock, and the replay path calls
 * `runRefresh`, which takes that same lock. A replay from inside would wait on the holder that is
 * waiting for it. `changePassword` calls `mintFreshBearer()` BEFORE it enters the lock, so the
 * expired-bearer case is already handled and the 401 that survives is a real refusal for the form.
 *
 * The two second-factor routes are public mint routes too (kernel apps, WS-9b, F-07). The
 * second-factor token is their credential, so a 401 there says nothing about a session this tab
 * holds. `useAuth.completeSecondFactor` also sends the verify inside the refresh lock, so a replay
 * from there would wait on its own holder.
 */
const NO_RETRY_PATHS: readonly string[] = [
  '/v1/auth/refresh',
  '/v1/auth/login',
  '/v1/auth/logout',
  '/v1/auth/passkey/login',
  '/v1/auth/passkey/login/options',
  '/v1/auth/oauth/exchange',
  '/v1/auth/change-password',
  '/v1/auth/second-factor/options',
  '/v1/auth/second-factor/verify',
  // The role switch posts inside the refresh lock too, after a pre-refresh outside it.
  ACTIVE_ROLE_SWITCH_PATH,
];

function isNoRetryPath(path: string): boolean {
  return NO_RETRY_PATHS.some((p) => path === p || path.startsWith(`${p}?`));
}

/**
 * A human-readable description of a caught value.
 *
 * ⛔ EXPORTED AND SEPARATE, rather than an inline ternary inside the `catch` below, and the reason
 * is coverage HONESTY rather than tidiness. `response.json()` always rejects with a real `Error`
 * (a `SyntaxError`), so the non-Error arm is unreachable THROUGH THAT CALL SITE — it would sit
 * permanently uncovered, and the only ways to green the number would be a suppression (banned) or
 * deleting the arm.
 *
 * Deleting it is the wrong answer: `catch (cause)` is typed `unknown` because JavaScript genuinely
 * permits `throw 'a string'`, and `String(cause)` is what keeps the resulting `ApiError` readable
 * instead of `undefined`. So the defensive branch stays and becomes directly testable.
 */
export function describeCause(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * `work`, or the signal's reason when the signal aborts first.
 *
 * `fetch` itself stops on an aborted signal, but two waits in `apiFetch` do not: the coalesced
 * refresh after a 401 and the read of a body whose headers already arrived. Each wait goes through
 * this helper, so an aborted call rejects with the signal's reason and never hangs. The shared
 * refresh keeps going for the other callers that wait on it.
 */
function untilAborted<T>(work: Promise<T>, signal: AbortSignal | undefined): Promise<T> {
  if (signal === undefined) return work;
  return new Promise<T>((resolve, reject) => {
    const stop = (): void => reject(signal.reason);
    if (signal.aborted) stop();
    signal.addEventListener('abort', stop, { once: true });
    work.then(
      (value) => {
        signal.removeEventListener('abort', stop);
        resolve(value);
      },
      (cause: unknown) => {
        signal.removeEventListener('abort', stop);
        reject(cause);
      },
    );
  });
}

async function parseBody<T>(response: Response, path: string, signal: AbortSignal | undefined): Promise<T> {
  // 204 and any other genuinely empty body: resolve `undefined`, do not try to parse.
  if (response.status === 204) return undefined as T;
  try {
    return (await untilAborted(response.json(), signal)) as T;
  } catch (cause) {
    // An abort during the read is the caller's own stop, never a bad response.
    signal?.throwIfAborted();
    // A 200 with an HTML or empty body is the shape that produces a generic "failed to
    // load" banner with every request showing 200 in the network tab and nothing in the
    // log to explain it. Name the path in the error so the call site and DevTools can both
    // reason about WHICH endpoint misbehaved.
    throw new ApiError(response.status, {
      message: `Non-JSON response from ${path}`,
      cause: describeCause(cause),
    });
  }
}

/**
 * The parsed body of a 2xx answer and the headers of that answer.
 *
 * Who reads it: a caller that needs a response header beside the body, such as the `ETag` of the
 * alert destinations (R-040 of plan 2026-10-28-alert-noise). `body` is the value that
 * {@link apiFetch} resolves for the same request: `undefined` for a 204, else the parsed JSON.
 * `headers` are the headers of the answer that `body` came from: the replay's after a 401 replay.
 */
export interface ApiAnswer<T> {
  readonly body: T;
  readonly headers: Headers;
}

/**
 * The options of a verb that answers {@link ApiAnswer}: a `signal`, as for the other verbs, and
 * extra request headers. An extra header goes on the request and on its 401 replay. The client
 * label and the bearer are set after it, so an extra header named exactly `x-zion-client` or
 * `Authorization` gives way to the client's own value.
 */
export interface ApiAnswerInit extends Pick<RequestInit, 'signal'> {
  readonly headers?: Readonly<Record<string, string>>;
}

/*
 * One dot segment of a URL path as the URL parser reads it: `.` or `..`, each dot also as `%2e` in
 * either case. `encodeURIComponent` leaves `.` and `..` unchanged, and `fetch` removes a dot segment
 * with the segment before it, so `/destinations/dp/..` reaches `/destinations/`.
 */
const DOT_SEGMENT = /^(?:\.|%2e){1,2}$/i;

/**
 * The rejection of a request whose path holds a dot segment. The client sends no request for it.
 * Its `message` is the sentence a pane shows.
 */
export const DOT_SEGMENT_PATH_MESSAGE = 'The console did not send the request: its path holds a "." or ".." segment.';

/* The code points 0x09, 0x0A and 0x0D: the URL parser removes each of them from any place in the input. */
const URL_STRIPPED_CODES: ReadonlySet<number> = new Set([0x09, 0x0a, 0x0d]);

/*
 * The segments of the path part of `path` as the URL parser reads it for an `http` or `https` URL:
 * 1. It trims each C0 control and each space (code point 0x20 or lower) from both ends.
 * 2. It removes each tab, line feed and carriage return.
 * 3. It ends the path at the first `?` or `#`.
 * 4. It takes `/` and `\` both as a separator.
 * Pure.
 */
function parsedPathSegments(path: string): readonly string[] {
  let start = 0;
  let end = path.length;
  while (start < end && path.charCodeAt(start) <= 0x20) start += 1;
  while (end > start && path.charCodeAt(end - 1) <= 0x20) end -= 1;
  let kept = '';
  for (let at = start; at < end; at += 1) if (!URL_STRIPPED_CODES.has(path.charCodeAt(at))) kept += path.charAt(at);
  return kept.replace(/[?#][\s\S]*$/, '').split(/[/\\]/);
}

/**
 * {@link apiFetch}, and the headers of the answer. Every rule of `apiFetch` holds: the base URL, the
 * bearer, the client label, one coalesced refresh, one replay, the abort and the errors. A non-2xx
 * answer rejects with {@link ApiError}, as `apiFetch` does, and gives no headers.
 *
 * A `path` whose path part holds a dot segment (`.`, `..`, or either with a dot written `%2e`)
 * rejects with an `Error` of {@link DOT_SEGMENT_PATH_MESSAGE} and sends no request. The check reads
 * the path as the URL parser does: a `\` also separates two segments, and a tab or a line break
 * inside a segment goes (`parsedPathSegments`). An id from a network answer goes into a path through
 * `encodeURIComponent`, which leaves `..` unchanged, and `fetch` sends a dot segment to the parent
 * route. An aborted `signal` rejects with its reason first, before the path check.
 */
export async function apiFetchAnswer<T>(path: string, options: RequestInit = {}): Promise<ApiAnswer<T>> {
  options.signal?.throwIfAborted();
  if (parsedPathSegments(path).some((segment) => DOT_SEGMENT.test(segment))) {
    throw new Error(DOT_SEGMENT_PATH_MESSAGE);
  }
  const url = `${getApiBaseUrl()}${path}`;
  const signal = options.signal ?? undefined;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
    // Last, so every request and its replay carry the console label (D10, F3).
    ...consoleClientHeaders(),
  };
  if (accessToken !== null) headers['Authorization'] = `Bearer ${accessToken}`;

  let response = await fetch(url, { ...options, headers });

  if (response.status === 401 && refreshFn !== null && !isNoRetryPath(path)) {
    const fresh = await untilAborted(coalescedRefresh(refreshFn), signal);
    if (fresh === null) {
      // ⛔ `LOGIN_URL`, never a literal. One definition in platform/paths.ts — see this
      // file's header for why the constant rather than the string.
      window.location.href = LOGIN_URL;
      throw new ApiError(401, { message: 'Session expired' });
    }
    accessToken = fresh;
    headers['Authorization'] = `Bearer ${fresh}`;
    // ⛔ Replayed EXACTLY ONCE. There is no loop here and there must not be: a 401 that
    // survives a successful refresh is a genuine denial, and retrying it is an unbounded
    // request loop against the auth service. The single replay is expressed by this being
    // straight-line code rather than a `while` — a flag would invite someone to reset it.
    response = await fetch(url, { ...options, headers });
  }

  if (!response.ok) {
    const failed = response;
    const body: unknown = await untilAborted(failed.json(), signal).catch(() => {
      // An abort during the read of the error body is the caller's own stop, never an HTTP error.
      signal?.throwIfAborted();
      return { message: failed.statusText };
    });
    throw new ApiError(failed.status, body);
  }

  return { body: await parseBody<T>(response, path, signal), headers: response.headers };
}

/** Core fetch wrapper: base URL, bearer header, client label, one coalesced refresh, one replay. */
export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  return (await apiFetchAnswer<T>(path, options)).body;
}

function withBody(method: string, body: unknown): RequestInit {
  return { method, body: body === undefined ? undefined : JSON.stringify(body) };
}

/**
 * Verb helpers. Every call in the shell goes through one of these. `get`, `post`, `put`, `patch`
 * and `delete` also take a `signal`, so a call can carry a time bound or stop when its dialog closes: an
 * aborted signal rejects the call with the signal's reason. That holds in each wait of the call: the
 * request, the refresh after a 401, and the read of the body, on a success and on an error status.
 */
export const api = {
  get: <T>(path: string, init?: Pick<RequestInit, 'signal'>): Promise<T> => apiFetch<T>(path, { ...init }),
  post: <T>(path: string, body?: unknown, init?: Pick<RequestInit, 'signal'>): Promise<T> =>
    apiFetch<T>(path, { ...withBody('POST', body), ...init }),
  put: <T>(path: string, body?: unknown, init?: Pick<RequestInit, 'signal'>): Promise<T> =>
    apiFetch<T>(path, { ...withBody('PUT', body), ...init }),
  patch: <T>(path: string, body?: unknown, init?: Pick<RequestInit, 'signal'>): Promise<T> =>
    apiFetch<T>(path, { ...withBody('PATCH', body), ...init }),
  delete: <T>(path: string, init?: Pick<RequestInit, 'signal'>): Promise<T> => apiFetch<T>(path, { method: 'DELETE', ...init }),
  /** `get`, and the headers of the answer ({@link apiFetchAnswer}). */
  getAnswer: <T>(path: string, init?: ApiAnswerInit): Promise<ApiAnswer<T>> => apiFetchAnswer<T>(path, { ...init, headers: { ...init?.headers } }),
  /** `put`, and the headers of the answer ({@link apiFetchAnswer}). */
  putAnswer: <T>(path: string, body: unknown, init?: ApiAnswerInit): Promise<ApiAnswer<T>> =>
    apiFetchAnswer<T>(path, { ...withBody('PUT', body), ...init, headers: { ...init?.headers } }),
};
